import React, {
  useEffect, useState, useCallback, useMemo, ReactElement,
} from 'react';
import Widget from '../../../components/widget/widget.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import WidgetPhotoSlider from '../../../components/widget-image-slider/widget-image-slider.component';
import WidgetImageSliderSimple
  from '../../../components/widget-image-slider-simple/widget-image-slider-simple.component';
import DealPhotosModal from '../deal-photos-modal/deal-photos-modal.component';
import StructureService from '../../../services/structure-service';
import { DEAL_PHOTO_CREATE, DEAL_PHOTO_DELETE, DEAL_PHOTO_EDIT } from '../../../constants/entity/permissions';
import AddNewImage from '../../../static/images/add-new-image.png';

const DealPhotos: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Deal Photos';
  const WIDGET_NAME: string = 'widget-deal-photos';

  const {
    isEditMode,
    isNewDeal,
    dealData,
    dealPhotoFiles,
    dealPhotoNewItems,
    dealPhotoList,
    onFilesChange,
    onFileDelete,
    onAddNewItem,
    onDeleteDealPhoto,
    onDealPhotoListChange,
    onEditModeButtonClick,
  } = props;

  const dealName: string = (dealData && dealData.name) || 'New Deal';
  const photoTextPrefix: string = dealName ? `${dealName} ` : '';

  const [isDealPhotoModalOpen, setIsDealPhotoModalOpen]: [boolean, Function] = useState(false);
  const [dealPhotoActiveId, setDealPhotoActiveId]: [number | null, Function] = useState(null);

  const photoSliderDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    dealPhotoList, dealPhotoNewItems, dealPhotoFiles,
  ], [dealPhotoList, dealPhotoNewItems, dealPhotoFiles]);

  const dealPhotoEditPermissions: string[] = useMemo((): string[] => [DEAL_PHOTO_EDIT], []);
  const dealPhotoCreatePermissions: string[] = useMemo((): string[] => [DEAL_PHOTO_CREATE], []);
  const dealPhotoDeletePermissions: string[] = useMemo((): string[] => [DEAL_PHOTO_DELETE], []);

  const dealPhotosTotal: number = (dealPhotoList.length || 0)
    + (isEditMode || isNewDeal ? dealPhotoNewItems.length : 0);
  const isEmpty: boolean = !isEditMode && !isNewDeal && (!dealPhotosTotal || !dealData);
  const formPrefix: string = 'dealData.dealPhotosData';

  const addNewItem: Function = useCallback((): void => {
    if (onAddNewItem) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealPhotoNewItems, dealPhotoList),
      });
    }
  }, [dealPhotoList, dealPhotoNewItems, dealPhotoFiles]);

  const openModal: Function = useCallback((photoItemId: number): void => {
    setIsDealPhotoModalOpen(true);
    setDealPhotoActiveId(photoItemId);
  }, []);

  const onCloseModal: Function = useCallback((): void => {
    setIsDealPhotoModalOpen(false);
    setDealPhotoActiveId(null);
  }, []);

  const onDropPhoto: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    if (dealPhotoList) {
      onDealPhotoListChange(StructureService.switchListItems(dealPhotoList, dragIndex, hoverIndex));
    }
  }, [dealPhotoList, dealPhotoNewItems]);

  const filesChange: Function = useCallback((files: File[], dealPhotoId: number): void => {
    if (onFilesChange) {
      onFilesChange(dealPhotoId, files);
    }
  }, [dealPhotoList, dealPhotoNewItems, dealPhotoFiles]);

  useEffect((): void => {
    if (dealPhotoNewItems && !dealPhotoNewItems.length) {
      addNewItem();
    }
  }, [dealPhotoNewItems]);

  return (
    <CheckAccess permissions={dealPhotoEditPermissions} isEnabled={isEditMode || isNewDeal}>
      {
        !isEmpty && (
          <Widget
            isFullWidth
            isFullHeight
            isEditMode={isEditMode}
            withEditMode={!isNewDeal}
            editPermissions={dealPhotoEditPermissions}
            widgetName={WIDGET_NAME}
            defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
            onEditModeButtonClick={onEditModeButtonClick}
          >
            <FlexContainer isCentered isColumn isFullWidth={isEditMode || isNewDeal}>
              {
                !isEditMode && !isNewDeal ? (
                  dealPhotoList && !!dealPhotoList.length && (
                    <WidgetImageSliderSimple
                      imageList={dealPhotoList}
                      imageTextPrefix={photoTextPrefix}
                      onSlideClick={openModal}
                    />
                  )
                ) : (
                  <CheckAccess permissions={dealPhotoEditPermissions}>
                    <WidgetPhotoSlider
                      imageList={dealPhotoList}
                      imageTextPrefix={photoTextPrefix}
                      formPrefix={formPrefix}
                      dropDeps={photoSliderDropDeps}
                      imageNewItems={dealPhotoNewItems}
                      imageFiles={dealPhotoFiles}
                      imageCreatePermissions={dealPhotoCreatePermissions}
                      imageDeletePermissions={dealPhotoDeletePermissions}
                      defaultPreviewUrl={AddNewImage}
                      onDropImage={onDropPhoto}
                      onDeleteImage={onDeleteDealPhoto}
                      onFileDelete={onFileDelete}
                      onFilesChange={filesChange}
                      inputNameRenderer={(fieldIndex: string): string => `${formPrefix}[${fieldIndex}].file`}
                    />
                  </CheckAccess>
                )
              }
            </FlexContainer>
            <DealPhotosModal
              isOpen={isDealPhotoModalOpen}
              dealData={dealData}
              dealPhotoActiveId={dealPhotoActiveId}
              dealPhotoList={dealPhotoList}
              onCloseModal={onCloseModal}
            />
          </Widget>
        )
      }
    </CheckAccess>
  );
});

export default DealPhotos;
